<script setup lang="ts">
/**
 * Off canvas navigation drawer, used below 900px where the 232px rail no longer
 * fits. It behaves as a modal dialog: the focus is trapped inside while it is
 * open, Escape and a click on the backdrop close it, the page behind is locked,
 * and the focus returns to the button that opened it.
 */
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    ouvert: boolean;
    /** Accessible name of the dialog. */
    titre?: string;
  }>(),
  { titre: 'Navigation principale' },
);

const emit = defineEmits<{
  fermer: [];
}>();

const panneau = ref<HTMLElement | null>(null);
let elementRestitue: HTMLElement | null = null;

const SELECTEUR_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function elementsFocusables(): HTMLElement[] {
  const racine = panneau.value;
  if (racine === null) {
    return [];
  }
  return Array.from(racine.querySelectorAll<HTMLElement>(SELECTEUR_FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/** Keep the Tab sequence inside the panel and close on Escape. */
function surTouche(evenement: KeyboardEvent): void {
  if (evenement.key === 'Escape') {
    evenement.preventDefault();
    emit('fermer');
    return;
  }
  if (evenement.key !== 'Tab') {
    return;
  }
  const cibles = elementsFocusables();
  const premier = cibles[0];
  const dernier = cibles[cibles.length - 1];
  if (premier === undefined || dernier === undefined) {
    evenement.preventDefault();
    return;
  }
  const actif = document.activeElement;
  const dedans = actif instanceof HTMLElement && panneau.value?.contains(actif) === true;
  if (evenement.shiftKey && (actif === premier || !dedans)) {
    evenement.preventDefault();
    dernier.focus();
    return;
  }
  if (!evenement.shiftKey && actif === dernier) {
    evenement.preventDefault();
    premier.focus();
  }
}

function verrouillerPage(verrou: boolean): void {
  document.body.classList.toggle('hz-body--verrouille', verrou);
}

watch(
  () => props.ouvert,
  async (ouvert) => {
    if (ouvert) {
      elementRestitue =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      verrouillerPage(true);
      await nextTick();
      const cibles = elementsFocusables();
      (cibles[0] ?? panneau.value)?.focus();
      return;
    }
    verrouillerPage(false);
    elementRestitue?.focus();
    elementRestitue = null;
  },
);

onBeforeUnmount(() => {
  verrouillerPage(false);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="ouvert"
      class="hz-backdrop"
      data-hz-role="fond-tiroir"
      @click="emit('fermer')"
    />
    <div
      v-if="ouvert"
      ref="panneau"
      class="hz-tiroir tiroir"
      role="dialog"
      aria-modal="true"
      :aria-label="props.titre"
      tabindex="-1"
      @keydown="surTouche"
    >
      <button
        type="button"
        class="tiroir__fermer"
        @click="emit('fermer')"
      >
        <svg
          viewBox="0 0 14 14"
          aria-hidden="true"
          focusable="false"
        >
          <line
            x1="2"
            y1="2"
            x2="12"
            y2="12"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
          <line
            x1="12"
            y1="2"
            x2="2"
            y2="12"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
        <span class="hz-visually-hidden">Fermer la navigation</span>
      </button>
      <slot />
    </div>
  </Teleport>
</template>

<style scoped>
.tiroir {
  position: fixed;
}

.tiroir__fermer {
  position: absolute;
  top: var(--hz-sp-3);
  right: var(--hz-sp-3);
  z-index: 1;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 1px solid var(--hz-border);
  border-radius: var(--hz-radius);
  background: var(--hz-bg-card);
  color: var(--hz-text-strong);
  line-height: 1;
  cursor: pointer;
}

.tiroir__fermer svg {
  width: 14px;
  height: 14px;
}

.tiroir__fermer:hover {
  background: var(--hz-bg-app);
}
</style>
